import { Zap } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEphemeralStartSpotTarget, useEphemeralStopSpotTarget } from "@/hooks/use-spot-targets";
import type { SpotTarget } from "@/lib/types";
import { cn } from "@/lib/utils";

const PRESET_HOURS = [1, 2, 4, 8, 24] as const;
const MAX_CUSTOM_HOURS = 72;

type PresetValue = (typeof PRESET_HOURS)[number] | "custom";

function formatTargetLabel(target: SpotTarget): string {
  const gpu = `${target.gpu_count}×${target.gpu_type.toUpperCase()}`;
  return `${gpu} · ${target.provider.toUpperCase()}`;
}

function formatUntilClock(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

function formatUntilDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
}

function formatDurationRelative(targetMs: number, nowMs: number): string {
  const delta = Math.max(0, targetMs - nowMs);
  const totalMinutes = Math.round(delta / 60000);
  if (totalMinutes <= 0) return "0m";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

function useTick(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(handle);
  }, [intervalMs]);
  return now;
}

function PresetChip({
  hours,
  active,
  onClick,
  children,
}: {
  hours: number | "custom";
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      data-preset={hours}
      onClick={onClick}
      className={cn(
        "inline-flex h-10 min-w-[3.25rem] items-center justify-center rounded-md border bg-card font-mono text-[13px] font-medium tracking-tight transition-all duration-100",
        "focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        active
          ? "border-primary/50 bg-primary/12 text-primary shadow-[0_0_0_1px_var(--color-primary)/35]"
          : "border-border text-foreground/75 hover:border-primary/30 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function TimelineStrip({ durationHours }: { durationHours: number }) {
  // 24h reference window; clamp so a 72h window doesn't break the layout.
  const fraction = Math.min(1, Math.max(0.02, durationHours / 24));
  const fillPercent = (fraction * 100).toFixed(2);
  return (
    <div aria-hidden className="relative h-[6px] w-full overflow-hidden rounded-full bg-muted">
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-primary"
        style={{ width: `${fillPercent}%` }}
      />
      <div className="absolute inset-y-0 w-px bg-primary/40" style={{ left: `${fillPercent}%` }} />
    </div>
  );
}

function parseCustomHours(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0 || value > MAX_CUSTOM_HOURS) return null;
  return value;
}

export function EphemeralStartDialog({ target }: { target: SpotTarget }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<PresetValue>(2);
  const [customHours, setCustomHours] = useState("3");
  const startMutation = useEphemeralStartSpotTarget();
  const stopMutation = useEphemeralStopSpotTarget();

  // Runs while the card is mounted — both the dialog body's "Runs until"
  // readout and the trigger chip's countdown are visible from this hook.
  const now = useTick(30000);

  const ephemeralUntilMs = target.ephemeral_until
    ? new Date(target.ephemeral_until).getTime()
    : null;
  const isActive = ephemeralUntilMs !== null && ephemeralUntilMs > now;

  const customParsed = preset === "custom" ? parseCustomHours(customHours) : null;
  const selectedHours: number | null = preset === "custom" ? customParsed : (preset as number);

  const projectedUntilDate = useMemo(() => {
    if (selectedHours === null) return null;
    return new Date(now + selectedHours * 60 * 60 * 1000);
  }, [now, selectedHours]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setPreset(2);
      setCustomHours("3");
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (selectedHours === null) return;
    startMutation.mutate(
      { id: target.id, durationHours: selectedHours },
      {
        onSuccess: (updated) => {
          const until = updated.ephemeral_until ? new Date(updated.ephemeral_until) : null;
          const untilLabel = until ? `${formatUntilClock(until)} UTC` : `${selectedHours}h`;
          toast.success(
            isActive
              ? `Extended to ${untilLabel}`
              : `Provisioning until ${untilLabel} — ${formatTargetLabel(target)}`,
          );
          setOpen(false);
        },
        onError: (error) =>
          toast.error(
            `Failed to start ephemeral window: ${
              error instanceof Error ? error.message : "unknown error"
            }`,
          ),
      },
    );
  };

  const handleStopNow = () => {
    stopMutation.mutate(target.id, {
      onSuccess: () => {
        toast.success(`Ephemeral override cleared — ${formatTargetLabel(target)}`);
        setOpen(false);
      },
      onError: (error) =>
        toast.error(
          `Failed to clear override: ${error instanceof Error ? error.message : "unknown error"}`,
        ),
    });
  };

  const isPending = startMutation.isPending || stopMutation.isPending;
  const isInvalidCustom = preset === "custom" && customParsed === null;

  // --- Trigger styling: surface the active override on the card too. ---
  const triggerClassName = cn(
    "h-7 gap-1.5 text-[12px]",
    isActive
      ? "border border-primary/50 bg-primary/10 text-primary hover:bg-primary/15"
      : "border border-border bg-card text-foreground/80 hover:bg-accent hover:text-foreground",
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            className={triggerClassName}
            data-ephemeral-trigger={isActive ? "active" : "idle"}
          />
        }
      >
        {isActive ? (
          <EphemeralActiveLabel ephemeralUntilMs={ephemeralUntilMs} now={now} />
        ) : (
          <>
            <Zap className="size-3.5" aria-hidden />
            <span>Start now</span>
          </>
        )}
      </DialogTrigger>
      <DialogContent className="border-border bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {isActive ? "Ephemeral override active" : "Start now"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isActive ? (
              <>
                <span className="font-mono text-foreground/80">{formatTargetLabel(target)}</span>
                {ephemeralUntilMs !== null && (
                  <>
                    {" "}
                    · ends{" "}
                    <span className="font-mono text-foreground/80">
                      {formatUntilClock(new Date(ephemeralUntilMs))} UTC
                    </span>
                    {" · "}
                    in {formatDurationRelative(ephemeralUntilMs, now)}
                  </>
                )}
              </>
            ) : (
              <>
                Wake the provisioner outside its schedule for{" "}
                <span className="font-mono text-foreground/80">{formatTargetLabel(target)}</span>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {isActive ? "Extend by" : "Duration"}
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_HOURS.map((hours) => (
                <PresetChip
                  key={hours}
                  hours={hours}
                  active={preset === hours}
                  onClick={() => setPreset(hours)}
                >
                  {hours}h
                </PresetChip>
              ))}
              <PresetChip
                hours="custom"
                active={preset === "custom"}
                onClick={() => setPreset("custom")}
              >
                Custom…
              </PresetChip>
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 pt-1.5">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0.25}
                  max={MAX_CUSTOM_HOURS}
                  step={0.25}
                  value={customHours}
                  onChange={(event) => setCustomHours(event.target.value)}
                  autoFocus
                  aria-invalid={isInvalidCustom}
                  className={cn(
                    "h-9 w-28 border-border bg-background font-mono text-[13px] text-foreground",
                    isInvalidCustom && "border-destructive ring-2 ring-destructive/30",
                  )}
                />
                <span className="font-mono text-[12px] text-muted-foreground">hours</span>
                {isInvalidCustom && (
                  <span className="text-[11px] text-destructive">
                    Must be 0 &lt; n &le; {MAX_CUSTOM_HOURS}
                  </span>
                )}
              </div>
            )}
          </div>

          {selectedHours !== null && projectedUntilDate !== null && (
            <div className="space-y-3 rounded-lg border border-border bg-accent/30 p-3">
              <TimelineStrip durationHours={selectedHours} />
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Runs until
                </span>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {selectedHours}h slice
                </span>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-mono">
                <span className="text-2xl font-semibold tracking-tight text-primary">
                  {formatUntilClock(projectedUntilDate)}
                </span>
                <span className="text-[11px] text-muted-foreground">UTC</span>
                <span className="text-muted-foreground/50">·</span>
                <span className="text-[12px] text-foreground/80">
                  {formatUntilDate(projectedUntilDate)}
                </span>
                <span className="text-muted-foreground/50">·</span>
                <span className="text-[12px] text-foreground/80">
                  in {formatDurationRelative(projectedUntilDate.getTime(), now)}
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-2 rounded-md border border-border/60 bg-muted/40 p-3 text-[12px] leading-5 text-muted-foreground">
            <Zap className="mt-0.5 size-3.5 shrink-0 text-primary/70" aria-hidden />
            <span>
              Only restarts provisioning. Existing machines keep running after the window ends — EOD
              reaper / idle release will drain them as usual.
            </span>
          </div>

          <DialogFooter className="flex-wrap items-center gap-2 sm:flex-row sm:justify-between">
            {isActive ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleStopNow}
                disabled={isPending}
                className="h-8 px-3"
              >
                Stop now
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="border-border text-foreground/70 hover:bg-accent"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending || selectedHours === null}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {startMutation.isPending
                  ? isActive
                    ? "Extending…"
                    : "Starting…"
                  : isActive
                    ? "Extend →"
                    : "Start now →"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EphemeralActiveLabel({
  ephemeralUntilMs,
  now,
}: {
  ephemeralUntilMs: number;
  now: number;
}) {
  return (
    <>
      <span
        aria-hidden
        className="size-1.5 animate-pulse rounded-full bg-primary shadow-[0_0_0_3px_var(--color-primary)/15]"
      />
      <span className="font-mono uppercase tracking-[0.14em]">Ephemeral</span>
      <span className="text-foreground/50">·</span>
      <span className="font-mono">{formatDurationRelative(ephemeralUntilMs, now)}</span>
    </>
  );
}

export function EphemeralStatusBadge({ target }: { target: SpotTarget }) {
  const now = useTick(30000);
  if (!target.ephemeral_until) return null;
  const untilMs = new Date(target.ephemeral_until).getTime();
  if (untilMs <= now) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
      <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-primary" />
      Ephemeral
      <span className="text-primary/70">·</span>
      <span className="normal-case tracking-normal">
        ends in {formatDurationRelative(untilMs, now)}
      </span>
    </span>
  );
}
